import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContract } from '../../context/ContractContext';
import ebesSolarPDFService from '../../utils/ebesSolarPDFService';
import { FileText, Download, AlertCircle, CheckCircle, Home, Plus } from 'lucide-react';

const EbesPDFGeneratorPage = () => {
  const navigate = useNavigate();
  const { contractData, clearContractData } = useContract();
  const [generating, setGenerating] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Verificar se há dados do contrato
    if (!contractData.ebesSolar) {
      navigate('/contratos/novo');
      return;
    }

    // Gerar PDF automaticamente ao carregar a página
    generatePDF();
  }, []);

  const generatePDF = async () => {
    setGenerating(true);
    setError(null);

    try {
      const pdfResult = await ebesSolarPDFService.generateEbesSolarContract(contractData);
      
      if (pdfResult.success) {
        setResult(pdfResult);
      } else {
        setError(pdfResult.message || 'Erro ao gerar PDF');
      }
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      setError('Erro inesperado ao gerar o contrato. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  };

  const handleNewContract = () => {
    clearContractData();
    navigate('/contratos/novo');
  };

  const handleBackToDashboard = () => {
    clearContractData();
    navigate('/dashboard');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (generating) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
          </div>
          <h2 className="text-xl font-semibold text-dark-900 mb-2">
            Gerando Contrato EBES Solar
          </h2>
          <p className="text-neutral-600">
            Aguarde enquanto preparamos seu contrato...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border p-8">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-dark-900 mb-2">
                  Erro ao Gerar Contrato
                </h1>
                <p className="text-neutral-600 mb-6">
                  {error}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={generatePDF}
                    className="flex items-center justify-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Tentar Novamente
                  </button>
                  <button
                    onClick={() => navigate('/contratos/novo')}
                    className="flex items-center justify-center px-6 py-3 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                  >
                    Voltar ao Formulário
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-dark-900 mb-2">
                Contrato Gerado com Sucesso!
              </h1>
              <p className="text-neutral-600">
                Seu contrato EBES Solar foi criado e está pronto.
              </p>
            </div>

            {/* Contract Summary */}
            <div className="bg-neutral-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-dark-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Resumo do Contrato
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-neutral-500">Tipo:</span>
                  <p className="font-medium">{contractData.ebesSolar?.clientType === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}</p>
                </div>
                
                <div>
                  <span className="text-neutral-500">Cliente:</span>
                  <p className="font-medium">{contractData.ebesSolar?.clientName}</p>
                </div>
                
                <div>
                  <span className="text-neutral-500">{contractData.ebesSolar?.clientType === 'pj' ? 'CNPJ' : 'CPF'}:</span>
                  <p className="font-medium">{contractData.ebesSolar?.clientType === 'pj' ? contractData.ebesSolar?.clientCNPJ : contractData.ebesSolar?.clientCPF}</p>
                </div>
                
                <div>
                  <span className="text-neutral-500">Potência da Usina:</span>
                  <p className="font-medium">{contractData.ebesSolar?.systemPower} kWp</p>
                </div>
                
                <div>
                  <span className="text-neutral-500">Valor Total:</span>
                  <p className="font-medium">{formatCurrency(contractData.ebesSolar?.totalValue)}</p>
                </div>
                
                <div>
                  <span className="text-neutral-500">Local de Instalação:</span>
                  <p className="font-medium">{contractData.ebesSolar?.installationCity}/{contractData.ebesSolar?.installationState}</p>
                </div>
                
                <div>
                  <span className="text-neutral-500">Data de Geração:</span>
                  <p className="font-medium">{new Date().toLocaleDateString('pt-BR')}</p>
                </div>
              </div>
            </div>

            {/* Download Section */}
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-6 mb-8">
              <div className="flex items-start">
                <Download className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 mb-2">
                    Download do Contrato
                  </h3>
                  <p className="text-yellow-700 text-sm mb-4">
                    O contrato foi baixado automaticamente. Caso não tenha iniciado o download, 
                    clique no botão abaixo para baixar novamente.
                  </p>
                  <button
                    onClick={generatePDF}
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Novamente
                  </button>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="mb-8">
              <h3 className="font-semibold text-dark-900 mb-4">Próximos Passos</h3>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Revise cuidadosamente todos os dados do contrato</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Imprima 2 vias do contrato para assinatura</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Colete as assinaturas de todas as partes envolvidas e testemunhas</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Protocole o pedido de acesso junto à concessionária</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span>Mantenha uma via assinada para seus registros</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleNewContract}
                className="flex items-center justify-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Novo Contrato
              </button>
              
              <button
                onClick={handleBackToDashboard}
                className="flex items-center justify-center px-6 py-3 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
              >
                <Home className="w-4 h-4 mr-2" />
                Voltar ao Dashboard
              </button>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-primary-800 mb-1">
                  Importante - Validação Jurídica
                </h4>
                <p className="text-sm text-primary-700">
                  Este contrato foi gerado automaticamente com base nas informações fornecidas. 
                  A EBES SOLAR recomenda sempre a revisão por um advogado especializado antes 
                  da assinatura, especialmente para projetos de grande porte.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EbesPDFGeneratorPage;